import axios from "axios";
import { message } from "antd";
import { checkToken } from "./auth";

// 扩展axios请求配置类型，添加重试计数器属性
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    retryCount?: number;
  }
  
  interface AxiosRequestConfig {
    retryCount?: number;
  }
}

// 最大重试次数
const MAX_RETRY_COUNT = 2;

// 可重试的错误类型
const RETRY_ERROR_CODES = ['ECONNABORTED', 'ETIMEDOUT', 'ENOTFOUND', 'NETWORK_ERROR'];

// 401错误计数器和阈值
let consecutive401Count = 0;
const MAX_401_COUNT = 5;
let last401Time = 0;

// 超时分级(PRD v4 05§1.6):常规请求 15s;
// 重建向量库/批量操作等长任务接口在调用处显式传入 LONG_TASK_TIMEOUT。
export const LONG_TASK_TIMEOUT = 180000;

const request = axios.create({
  timeout: 15000,
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 添加重试计数器
    if (!config.retryCount) {
      config.retryCount = 0;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    // 成功响应时重置401计数器
    consecutive401Count = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // 处理网络错误和超时等问题，尝试重试
    if (!error.response && originalRequest && originalRequest.retryCount < MAX_RETRY_COUNT) {
      const errorCode = error.code || 'NETWORK_ERROR';
      if (RETRY_ERROR_CODES.includes(errorCode)) {
        originalRequest.retryCount = (originalRequest.retryCount || 0) + 1;
        console.log(`网络请求错误，正在第 ${originalRequest.retryCount} 次重试...`);
        return new Promise(resolve => setTimeout(() => resolve(request(originalRequest)), 1000));
      }
    }
    
    // 只有在收到401状态码时才考虑token问题
    if (error.response?.status === 401) {
      const token = localStorage.getItem("jwt_token");
      const currentTime = Date.now();
      
      // 如果与上次401错误时间超过30秒，重置计数器
      if (currentTime - last401Time > 30000) {
        consecutive401Count = 0;
      }
      
      // 更新最后一次401时间
      last401Time = currentTime;
      
      // 如果没有token，或者token确实已经过期，才跳转登录页
      if (!token || !checkToken(token)) {
        // token 过期或无效，清除token并跳转
        localStorage.removeItem("jwt_token");
        
        // 检查当前页面是否是登录页，避免无限刷新
        if (!window.location.pathname.includes('/login')) {
          // 保存当前URL，登录后可以重定向回来
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== '/') {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
          }
          
          // 跳转到登录页
          window.location.href = "/console/login";
        }
      } else if (originalRequest) {
        // token存在且有效，但服务器返回401，可能是暂时性问题
        consecutive401Count++;
        console.warn(`服务器拒绝请求但本地token有效，当前连续401计数: ${consecutive401Count}`);
        
        if (consecutive401Count >= MAX_401_COUNT) {
          // 超过阈值，可能是服务端session过期或被踢下线
          message.warning({
            content: "您的登录状态可能已失效，请尝试刷新页面或重新登录",
            key: "session-warning",
            duration: 10
          });
          // 添加一个小延迟避免过于频繁的提示
          last401Time = currentTime + 60000; // 下一次最少60秒后才会再提示
        } else if (!originalRequest.retryCount || originalRequest.retryCount < 1) {
          // 尝试一次重试，而不是立即跳转登录页
          originalRequest.retryCount = (originalRequest.retryCount || 0) + 1;
          console.log("服务器拒绝请求但本地token有效，尝试重试...");
          return request(originalRequest);
        }
      }
    } else if (error.response?.status === 429) {
      const msg =
        error.response?.data?.msg || "请求频率过高，触发风控机制，请稍后再试";
      message.error({
        content: `风控警告：${msg}`,
        key: "rate-limit-429",
        duration: 10,
      });
    } else {
      // 非401/429错误，重置401计数器
      consecutive401Count = 0;
    }
    
    return Promise.reject(error);
  }
);

export default request;
